import { useState } from "react";
import { FileText, Mail, Image as ImageIcon, BookOpen, MessageSquare, Table as TableIcon, BarChart3, Megaphone, PenLine } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { TaskText } from "./TaskText";
import type { TaskSource } from "@shared/performanceTasks";

const KIND_ICON: Record<TaskSource["kind"], typeof FileText> = {
  article: FileText, letter: Mail, poster: ImageIcon, poem: BookOpen, passage: BookOpen, interview: MessageSquare, comments: MessageSquare,
  table: TableIcon, graph: BarChart3, diagram: ImageIcon, notice: Megaphone, advertisement: Megaphone, email: Mail, data: TableIcon,
};
const KIND_LABEL: Record<TaskSource["kind"], string> = {
  article: "Article", letter: "Letter", poster: "Poster", poem: "Poem", passage: "Passage", interview: "Interview", comments: "Reader comments",
  table: "Table", graph: "Graph", diagram: "Diagram", notice: "Notice", advertisement: "Advertisement", email: "Email", data: "Data",
};
const BAR_COLORS = ["bg-blue-500", "bg-emerald-500", "bg-orange-500", "bg-violet-500", "bg-rose-500", "bg-amber-500"];

export function SourceChart({ chart }: { chart: NonNullable<TaskSource["chart"]> }) {
  const max = Math.max(...chart.series.map(s => s.value), 1);
  return (
    <figure className="my-3 rounded-xl border border-border bg-card p-3">
      {chart.title && <figcaption className="text-sm font-semibold mb-2">{chart.title}</figcaption>}
      <div className="flex items-end gap-3 h-40 px-1">
        {chart.series.map((s, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
            <span className="text-xs font-bold tabular-nums">{s.value}</span>
            <div className={`w-full max-w-[56px] rounded-t-md ${s.color ? "" : BAR_COLORS[i % BAR_COLORS.length]}`} style={{ height: `${Math.max(4, (s.value / max) * 100)}%`, backgroundColor: s.color }} aria-label={`${s.label}: ${s.value}`} />
            <span className="text-[11px] text-center leading-tight text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>
      {chart.yLabel && <p className="text-[11px] text-muted-foreground mt-2">{chart.yLabel}</p>}
    </figure>
  );
}

export function SourceCard({ source, note, onNote, notesEnabled }: { source: TaskSource; note?: string; onNote?: (v: string) => void; notesEnabled?: boolean }) {
  const Icon = KIND_ICON[source.kind] || FileText;
  return (
    <article className="space-y-2" data-testid={`source-${source.id}`}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold"><Icon className="w-3.5 h-3.5" /> {KIND_LABEL[source.kind]}</span>
        <h3 className="font-bold text-base">{source.title}</h3>
      </div>
      <div className={`rounded-xl border border-border p-4 text-sm ${source.kind === "poem" || source.kind === "letter" ? "bg-amber-50/60 dark:bg-amber-900/10" : "bg-card"}`}>
        {source.image && <img src={source.image.url} alt={source.image.alt} className="rounded-lg mb-3 max-h-80 object-contain mx-auto" loading="lazy" />}
        <TaskText text={source.body} />
        {source.chart && <SourceChart chart={source.chart} />}
        {source.attribution && <p className="text-[11px] text-muted-foreground mt-3 italic">{source.attribution}</p>}
      </div>
      {notesEnabled && onNote && (
        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3">
          <label className="text-xs font-semibold text-primary flex items-center gap-1 mb-1"><PenLine className="w-3.5 h-3.5" /> My important points from this source (not marked)</label>
          <Textarea value={note ?? ""} onChange={e => onNote(e.target.value)} rows={3} placeholder="Jot down the facts and ideas you might use..." className="bg-background text-sm" data-testid={`notes-${source.id}`} />
        </div>
      )}
    </article>
  );
}

export function SourceViewer({ sources, notes, onNote, notesEnabled }: { sources: TaskSource[]; notes?: Record<string, string>; onNote?: (id: string, v: string) => void; notesEnabled?: boolean }) {
  const [active, setActive] = useState(0);
  if (!sources.length) return null;
  const src = sources[Math.min(active, sources.length - 1)];
  return (
    <div className="space-y-3">
      {sources.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {sources.map((s, i) => (
            <button key={s.id} type="button" onClick={() => setActive(i)}
              className={`rounded-full px-3 py-1 text-sm font-semibold border-2 transition-colors ${i === active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
              data-testid={`source-tab-${s.id}`}>
              Source {i + 1}
            </button>
          ))}
        </div>
      )}
      <SourceCard source={src} note={notes?.[src.id]} onNote={onNote ? v => onNote(src.id, v) : undefined} notesEnabled={notesEnabled} />
    </div>
  );
}
